import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSportConfig } from '../context/SportConfig';
import { Link } from 'react-router-dom';
import AdminUsers from './AdminUsers';
import AdminPlayers from './AdminPlayers';
import AdminTournaments from './AdminTournaments';
import '../styles/Dashboard.css';

function Dashboard() {
  const { user, logout, token } = useAuth();
  const sportConfig = useSportConfig();
  const navTitle = sportConfig ? `${sportConfig.appEmoji} ${sportConfig.appName}` : '…';
  const [yourTurnLeagues, setYourTurnLeagues] = useState([]);

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  // Check all snake draft leagues to see if it's the user's turn
  useEffect(() => {
    if (!user || !token) return;

    const checkSnakeTurns = async () => {
      try {
        const leaguesRes = await fetch(`${API_URL}/leagues`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const leaguesData = await leaguesRes.json();
        if (!leaguesData.success) return;

        const snakeDrafting = leaguesData.leagues.filter(
          l => l.draftType === 'snake' && l.draftStatus === 'drafting'
        );
        if (snakeDrafting.length === 0) return;

        const results = await Promise.all(
          snakeDrafting.map(async l => {
            try {
              const res = await fetch(`${API_URL}/snake/${l._id}/state`, {
                headers: { Authorization: `Bearer ${token}` },
              });
              const data = await res.json();
              if (data.success && data.state.currentDrafterId === user._id) {
                return l;
              }
            } catch {}
            return null;
          })
        );

        setYourTurnLeagues(results.filter(Boolean));
      } catch {}
    };

    checkSnakeTurns();
    const interval = setInterval(checkSnakeTurns, 60000);
    return () => clearInterval(interval);
  }, [user, token, API_URL]);

  return (
    <div className="dashboard-container">
      <nav className="dashboard-nav">
        <div className="nav-left">
          <h1 className="nav-title">{navTitle}</h1>
        </div>
        <div className="nav-right">
          <span className="user-welcome">Hello, {user?.firstName || user?.username}!</span>
          <button onClick={logout} className="btn btn-logout">
            Logout
          </button>
        </div>
      </nav>

      <div className="dashboard-content">
        <header className="dashboard-header">
          <h2>Welcome to Your Dashboard</h2>
          <p>You're logged in! Coming soon: Create leagues, draft players, and compete.</p>
        </header>

        {yourTurnLeagues.length > 0 && (
          <div className="your-turn-banner">
            <span className="your-turn-icon">⭐</span>
            <div className="your-turn-text">
              <strong>It's your turn to draft!</strong>
              {yourTurnLeagues.map(l => (
                <Link key={l._id} to={`/leagues/${l._id}/snake-draft`} className="your-turn-link">
                  🐍 {l.name}
                </Link>
              ))}
            </div>
          </div>
        )}

        <div className="dashboard-grid">
          <div className="dashboard-card">
            <h3>📊 My Leagues</h3>
            <p>Join existing leagues or create new ones to start drafting.</p>
            <Link to="/leagues" className="btn btn-primary">Browse Leagues</Link>
          </div>

          <div className="dashboard-card">
            <h3>👥 My Teams</h3>
            <p>Manage your drafted teams and track your rankings.</p>
            <Link to="/teams" className="btn btn-primary">View Teams</Link>
          </div>

          {/* Draft Players card hidden — drafting is done from within a league */}

          <div className="dashboard-card">
            <h3>🏆 Leaderboards</h3>
            <p>See how you rank against other managers worldwide.</p>
            <Link to="/leaderboards" className="btn btn-primary">View Standings</Link>
          </div>
        </div>

        <section className="dashboard-section">
          <h3>Your Profile</h3>
          <div className="profile-info">
            <p><strong>Username:</strong> {user?.username}</p>
            <p><strong>Email:</strong> {user?.email}</p>
            {user?.firstName && <p><strong>Name:</strong> {user?.firstName} {user?.lastName}</p>}
            <p><strong>Member Since:</strong> {new Date(user?.createdAt).toLocaleDateString()}</p>
          </div>
        </section>

        {user?.isSiteAdmin && (
          <section className="dashboard-section">
            <h3>🛡️ Site Admin Tools</h3>
            <div className="dashboard-grid">
              <div className="dashboard-card">
                <h3>🏟️ All Leagues</h3>
                <p>View all leagues running on the platform — read-only access to details, rosters, and standings.</p>
                <Link to="/admin/leagues" className="btn btn-primary">View All Leagues</Link>
              </div>
              <div className="dashboard-card">
                <h3>📅 Tournaments</h3>
                <p>Add, edit, or delete tournaments. Past tournaments are hidden from the commissioner pick list automatically.</p>
                <Link to="/admin/tournaments" className="btn btn-primary">Manage Tournaments</Link>
              </div>
            </div>
          </section>
        )}
        {user?.isSiteAdmin && <AdminUsers />}
        {user?.isSiteAdmin && <AdminPlayers />}
        {user?.isSiteAdmin && <AdminTournaments />}
      </div>
    </div>
  );
}

export default Dashboard;
